import { GetStaticPathsResult, GetStaticProps, GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import Profile from '../../components/profile/Profile'
import { toastMessage } from '../../components/toastMessage'
import withDiaperBank from '../../components/withDiaperBank'
import { loadActiveDiaperBank } from '../../lib/withDiaperBankAuthentication'
import Context from '../../utils/Context'
import DiaperBankDTO, { DiaperBankPublic } from '../../utils/DiaperBankDTO'
import { getPrismaClient } from '../../utils/PrismaClient'
import { useDiaperBank } from '../../utils/useDiaperBank'
import { CustomHeadTags } from '../_app'

const BankProfile = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const context = useContext(Context)
  const router = useRouter()

  let staticDiaperBank = JSON.parse(props.diaperBank) as DiaperBankPublic
  // Even if the user is an admin, we want to initially show the speedy static version of the page before re-loading the most recent data, so we initialize
  // diaperBank to the static page's information.
  const [diaperBank, setDiaperBank] = useState<DiaperBankPublic>(staticDiaperBank as DiaperBankPublic)

  // If the user is admin of this diaper bank, re-fetch the diaper bank so that the admin gets the most up-to-date information possible after potentially
  // editing their diaper bank's information.

  let fetchAdminData = false
  if (context.jwtToken?.ownedDiaperBankSlugs?.includes(diaperBank.slug)) {
    fetchAdminData = true
  }

  const { data: adminData } = useDiaperBank({
    apiClient: context.apiClient,
    slug: diaperBank.slug,
    enabled: fetchAdminData,
    onError: (err) => {
      console.error(err)
      context.clearAccessToken()
      toastMessage({ title: "There was an error fetching your diaper fund's admin page", body: `Please try refreshing your browser`, type: 'error' })
      router.push('/')
    },
    role: 'admin',
  })

  // update UI with admin diaper bank by replacing the diaperBank state
  useEffect(() => {
    if (adminData?.data) {
      setDiaperBank(adminData.data)
      // indicate to the app (including this page) that the user is now viewing the app in an admin role
      context.setRole('admin')
    }
  }, [adminData?.data])

  const userIsAdminOfDiaperBank = context.jwtToken?.ownedDiaperBankSlugs?.includes(diaperBank.slug)
  const userHasChosenToSeePublicView = router.query.public === '1'
  const showPublicView = context.role === 'public' || userHasChosenToSeePublicView || !userIsAdminOfDiaperBank

  return <div>{<Profile diaperBank={diaperBank} isPublic={showPublicView} />}</div>
}

export default withDiaperBank(BankProfile)

export const getStaticProps: GetStaticProps<StaticProps> = async (context: GetStaticPropsContext) => {
  const slug = context.params?.slug ? `${context.params?.slug}` : undefined
  if (!slug) return { notFound: true }

  const prisma = getPrismaClient()
  const diaperBank = await loadActiveDiaperBank(prisma, slug)
  if (!diaperBank) return { notFound: true }

  const dto = await new DiaperBankDTO(diaperBank, 'public').getData()

  // props must be JSON serializable, so we turn it into a string to be parsed on the client
  const stringifiedDiaperBank = JSON.stringify(dto)

  const title = `${dto.babyName}'s Diaper Fund - Hello Bello`
  const customHeadTags: Partial<CustomHeadTags> = {
    title,
    ogTitle: title,
    description: dto.description || '',
    ogDescription: dto.description || '',
    ogImage: diaperBank.profileImagePath
      ? `${process.env.IMG_HOST}/${diaperBank.profileImagePath}?w=400&crop=entropy&h=400&ar=1:1&fit=crop`
      : `${process.env.HOST}/default_profile_pic.png`,
    canonicalUrl: dto.shareUrl,
    ogUrl: dto.shareUrl,
  }

  return {
    props: {
      diaperBank: stringifiedDiaperBank,
      customHeadTags,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<{ slug: string }>> {
  return {
    // because we have a decent number of diaper banks at this point, statically generating all of the pages at build time is inefficient, so we rely
    // exclusively on ISR to generate the pages for us
    paths: [],
    fallback: 'blocking',
  }
}

interface StaticProps {
  /** Stringified  */
  diaperBank: string
}
